import React, { useEffect, useState } from 'react';
import { heroData } from '../data/mock';
import { ChevronDown } from 'lucide-react';

const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col overflow-hidden bg-white">
      {/* Italian Flag Accent - Top */}
      <div className="absolute top-0 left-0 right-0 h-1 italian-stripe" />

      {/* Big Centered Logo */}
      <div className={`pt-8 pb-6 flex justify-center transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-10'
      }`}>
        <img
          src="/images/logo_final.jpeg"
          alt="CRISPYzza by Chef Francesco"
          className="h-96 md:h-[28rem] lg:h-[32rem] w-auto object-contain origin-center transition-transform duration-1000 scale-100 md:scale-[2]"
        />
      </div>

      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.02] pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%231e1919' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 flex-1 flex items-center max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center w-full">
          {/* Left - Text Content */}
          <div
            className={`transition-all duration-1000 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <span className="inline-block text-[#c9a962] text-sm font-semibold tracking-[0.2em] uppercase mb-6">
              {heroData.tagline}
            </span>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold text-[#1e1919] leading-[1.1] mb-4">
              {heroData.headline}
            </h1>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-8">
              <span className="gradient-text">This is CRISPYzza</span>
            </h2>
            <p className="text-lg md:text-xl text-[#736c64] max-w-lg mb-10 leading-relaxed">
              {heroData.description}
            </p>

            <div className="flex flex-wrap gap-4">
              <a
                href="#menu"
                className="bg-[#c9a962] text-[#1e1919] px-8 py-4 text-base font-semibold rounded transition-all duration-300 hover:bg-[#61525a] hover:text-white hover:shadow-xl hover:shadow-[#c9a962]/20 hover:-translate-y-1"
              >
                {heroData.ctaText}
              </a>
              <a
                href="#chef"
                className="border-2 border-[#1e1919] text-[#1e1919] px-8 py-4 text-base font-semibold rounded transition-all duration-300 hover:bg-[#1e1919] hover:text-white"
              >
                Meet The Chef
              </a>
            </div>

            {/* Features Pills */}
            <div className="flex flex-wrap gap-3 mt-12">
              {['Soy-Based', 'Protein-Rich', 'Halal', 'Made in Italy'].map(
                (feature) => (
                  <span
                    key={feature}
                    className="px-4 py-2 bg-[#f8f8f8] text-sm font-medium text-[#61525a] rounded-full border border-[#e8e3db] shadow-sm"
                  >
                    {feature}
                  </span>
                )
              )}
            </div>
          </div>

          {/* Right - Hero Image */}
          <div
            className={`relative transition-all duration-1000 delay-500 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
            }`}
          >
            <div className="relative">
              {/* Background decoration */}
              <div className="absolute -inset-4 bg-gradient-to-br from-[#c9a962]/20 via-transparent to-[#009246]/10 rounded-3xl blur-2xl" />
              
              {/* Main Image */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="/images/image2.jpg"
                  alt="CRISPYzza Gourmet Collection"
                  className="w-full h-[450px] lg:h-[520px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1e1919]/40 via-transparent to-transparent" />
              </div>

              {/* Floating Badge */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-5 shadow-xl border border-[#f0f0f0]">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#c9a962]/10 rounded-full flex items-center justify-center">
                    <span className="text-2xl">🇮🇹</span>
                  </div>
                  <div>
                    <p className="text-sm text-[#736c64]">Authentic</p>
                    <p className="font-display text-lg font-bold text-[#1e1919]">
                      Italian Craft
                    </p>
                  </div>
                </div>
              </div>

              {/* Chef Badge */}
              <div className="absolute -top-4 -right-4 bg-[#1e1919] text-white rounded-xl px-5 py-3 shadow-xl">
                <p className="text-xs text-[#c9a962] font-medium">By</p>
                <p className="font-display font-semibold">Chef Francesco</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="pb-8 flex justify-center">
        <a
          href="#about"
          className="flex flex-col items-center text-[#736c64] hover:text-[#c9a962] transition-colors animate-bounce"
        >
          <span className="text-xs uppercase tracking-widest mb-2">Scroll</span>
          <ChevronDown size={20} />
        </a>
      </div>
    </section>
  );
};

export default Hero;
